import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ChevronRight, X, Users } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../../components/AppShell";
import { SectionTabs } from "../../components/leads/SectionTabs";
import { api, formatErr } from "../../lib/api";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", age: "", position: "", team: "", monthly_price_eur: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/clients"); setClients(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) {
      toast.error("Nombre, email y contraseña (6+) son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/clients", {
        ...form,
        age: form.age ? parseInt(form.age) : null,
        monthly_price_eur: form.monthly_price_eur ? parseFloat(form.monthly_price_eur) : null,
      });
      toast.success("Cliente creado");
      setOpen(false);
      setForm({ name: "", email: "", password: "", phone: "", age: "", position: "", team: "", monthly_price_eur: "" });
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <AppShell>
      <SectionTabs active="clients" />
      <div className="qv-page-head" data-testid="admin-clients-page">
        <div className="label">Clientes</div>
        <h1>Tu plantilla</h1>
        <p className="sub">Gestiona los jugadores que entrenas. Crea, edita y revisa su evolución.</p>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div style={{ color: "#737373", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase" }}>
          {clients.length} {clients.length === 1 ? "cliente" : "clientes"}
        </div>
        <button onClick={() => setOpen(true)} className="qv-btn-primary" data-testid="add-client-btn">
          <Plus size={16} /> AÑADIR CLIENTE
        </button>
      </div>

      {loading ? (
        <div style={{ color: "#737373", padding: 40 }}>Cargando...</div>
      ) : clients.length === 0 ? (
        <div className="qv-empty">
          <Users size={32} style={{ margin: "0 auto 14px", color: "#737373" }} />
          <div style={{ marginBottom: 18 }}>Aún no tienes clientes</div>
          <button onClick={() => setOpen(true)} className="qv-btn-primary">
            <Plus size={16} /> CREAR EL PRIMERO
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {clients.map((c) => (
            <Link key={c.id} to={`/app/admin/clients/${c.id}`} className="qv-row" data-testid={`client-${c.id}`}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: "#737373" }}>{c.email}</div>
              </div>
              <div style={{ fontSize: 12, color: "#a3a3a3" }}>{c.position || "—"}</div>
              <div style={{ fontSize: 12, color: "#a3a3a3" }}>{c.team || "—"}</div>
              <span className={`qv-pill ${c.subscription_status}`}>{c.subscription_status}</span>
              <ChevronRight size={16} color="#737373" />
            </Link>
          ))}
        </div>
      )}

      {open && (
        <div className="qv-modal-bg" onClick={() => setOpen(false)}>
          <form className="qv-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="add-client-modal">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontFamily: "Bebas Neue", fontSize: "1.8rem" }}>NUEVO CLIENTE</h3>
              <button type="button" onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", color: "#737373", cursor: "pointer" }}><X size={20} /></button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}><label>Nombre*</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" /></div>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}><label>Email*</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" /></div>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}><label>Contraseña inicial*</label><input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="6+ caracteres" data-testid="client-password" /></div>
              <div className="qv-field"><label>Teléfono</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="qv-field"><label>Edad</label><input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} /></div>
              <div className="qv-field"><label>Posición</label><input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Ej: Delantero" /></div>
              <div className="qv-field"><label>Equipo</label><input value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} /></div>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}><label>Precio mensual (€)</label><input type="number" step="0.01" value={form.monthly_price_eur} onChange={(e) => setForm({ ...form, monthly_price_eur: e.target.value })} placeholder="60" /></div>
            </div>
            <button type="submit" disabled={saving} className="qv-btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 22 }} data-testid="client-create-submit">
              {saving ? "CREANDO..." : "CREAR CLIENTE"}
            </button>
          </form>
        </div>
      )}
    </AppShell>
  );
}
